import { Injectable } from '@angular/core';

/**
 * Service for handling PDF downloads with cross-platform support
 * Provides special handling for iOS devices where traditional downloads don't work
 */
@Injectable({
  providedIn: 'root',
})
export class PdfDownloadService {
  
  private readonly BLOB_CLEANUP_DELAY_MS = 1000;
  
  /**
   * Downloads a PDF file with better cross-platform support, especially for iOS devices
   * @param url - The URL of the PDF to download
   * @param fileName - The suggested filename for the download
   * @param openInNewTab - Whether to open in new tab instead of downloading (default: false)
   */
  downloadPdf(url: string, fileName?: string, openInNewTab = false): void {
    if (!url) {
      throw new Error('URL is required for PDF download');
    }

    // Sanitize filename if provided
    const sanitizedFileName = fileName ? this.sanitizeFileName(fileName) : undefined;
    
    if (this.isIOSDevice()) {
      this.handleIOSDownload(url, sanitizedFileName);
    } else {
      // For non-iOS devices, either download or open based on preference
      this.handleStandardDownload(url, sanitizedFileName, openInNewTab);
    }
  }

  /**
   * Downloads a PDF from a Blob object
   * @param blob - The PDF blob data
   * @param fileName - The filename for the download
   * @param openInNewTab - Whether to open in new tab instead of downloading (default: false)
   */
  downloadPdfFromBlob(blob: Blob, fileName: string, openInNewTab = false): void {
    if (!blob) {
      throw new Error('Blob is required for PDF download');
    }
    
    if (!fileName) {
      throw new Error('Filename is required for blob download');
    }

    const sanitizedFileName = this.sanitizeFileName(fileName);
    const url = window.URL.createObjectURL(blob);
    
    // Schedule cleanup
    this.scheduleBlobCleanup(url);
    
    if (this.isIOSDevice()) {
      this.handleIOSDownload(url, sanitizedFileName);
    } else {
      // For non-iOS devices, either download or open based on preference
      this.handleStandardDownload(url, sanitizedFileName, openInNewTab);
    }
  }

  /**
   * Opens a PDF in a new tab/window for viewing
   * @param url - The URL of the PDF to view
   * @returns boolean indicating if window was opened successfully
   */
  viewPdf(url: string): boolean {
    if (!url) {
      throw new Error('URL is required for PDF viewing');
    }

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    return !!newWindow;
  }

  /**
   * Opens a PDF from Blob in a new tab/window for viewing
   * @param blob - The PDF blob data
   * @returns boolean indicating if window was opened successfully
   */
  viewPdfFromBlob(blob: Blob): boolean {
    if (!blob) {
      throw new Error('Blob is required for PDF viewing');
    }

    const url = window.URL.createObjectURL(blob);
    
    // Schedule cleanup after a longer delay since user might still be viewing
    setTimeout(() => {
      try {
        window.URL.revokeObjectURL(url);
      } catch {
        // Silently fail - URL may have already been revoked
      }
    }, 60000); // 60 seconds delay for viewing
    
    return this.viewPdf(url);
  }

  /**
   * Handles PDF download for iOS devices
   */
  private handleIOSDownload(url: string, fileName?: string): void {
    // iOS doesn't support download attribute, open in new tab
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    
    if (!newWindow) {
      // Popup was blocked, fallback to link method
      this.createDownloadLink(url, fileName);
    }
  }

  /**
   * Handles PDF download for standard devices (non-iOS)
   * Either downloads to Downloads folder OR opens in new tab based on preference
   */
  private handleStandardDownload(url: string, fileName?: string, openInNewTab = false): void {
    if (openInNewTab) {
      // Open in new tab for immediate viewing
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        // If popup blocked, fallback to download
        console.warn('Popup blocked, falling back to download');
        this.createDownloadLink(url, fileName);
      }
    } else {
      // Download to Downloads folder
      this.createDownloadLink(url, fileName);
    }
  }

  /**
   * Creates a temporary download link and triggers click
   * @param url - The URL to download from
   * @param fileName - Optional filename
   */
  private createDownloadLink(url: string, fileName?: string): void {
    const link = document.createElement('a');
    link.href = url;
    
    // Set attributes for security and compatibility
    link.rel = 'noopener noreferrer';
    
    // Set download attribute to force download to Downloads folder
    if (fileName) {
      link.download = fileName;
      // Don't set target='_blank' when we want to download to file system
    } else {
      // If no filename, open in new tab
      link.target = '_blank';
    }
    
    // Use a hidden link approach for better compatibility
    link.style.display = 'none';
    link.style.position = 'absolute';
    link.style.left = '-9999px';
    
    // Append to body temporarily (required for Firefox and Safari)
    document.body.appendChild(link);
    
    try {
      // Trigger click to start download
      link.click();
    } finally {
      // Clean up immediately for downloads, delayed for navigation
      const cleanupDelay = fileName ? 0 : 100;
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
      }, cleanupDelay);
    }
  }

  /**
   * Schedules cleanup of blob URLs to prevent memory leaks
   */
  private scheduleBlobCleanup(url: string): void {
    setTimeout(() => {
      try {
        window.URL.revokeObjectURL(url);
      } catch {
        // Silently fail - URL may have already been revoked
      }
    }, this.BLOB_CLEANUP_DELAY_MS);
  }

  /**
   * Sanitizes filename to prevent security issues
   */
  private sanitizeFileName(fileName: string): string {
    // Remove any path components and dangerous characters
    return fileName
      .replace(/[^a-zA-Z0-9.\-_ ]/g, '') // Keep only safe characters
      .replace(/\.\./g, '') // Remove directory traversal attempts
      .trim()
      .substring(0, 255); // Limit length
  }

  /**
   * Detects if the user is on an iOS device
   * @returns true if iOS device, false otherwise
   */
  private isIOSDevice(): boolean {
    // Check for iOS devices (iPhone, iPad, iPod)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                  !('MSStream' in window);
    
    // Also check for iPad on iOS 13+ (which reports as Mac)
    const isIPadOS = navigator.maxTouchPoints !== undefined && 
                     navigator.maxTouchPoints > 2 && 
                     navigator.userAgent.includes('Mac');
    
    return isIOS || isIPadOS;
  }

}